import { getConfig } from "../../client/config";
// solana-sign-in-message.ts - the Sign In With Solana (SIWS) text the BROWSER lane signs.
//
// The legacy genSolanaMessage text stamps `Domain: server` / `Origin: server`, so a
// signature over it proves who owns the wallet and nothing about which page asked. The
// issuer used to compensate with the request's Origin header, which any non-browser client
// can forge (SEC-01): an attacker's backend relayed a signature phished on a fake page with
// `Origin: https://auth.bounded.sh` and minted the victim's session. The standard SIWS text
// carries the requesting page's host in its first line. Bounded leaves that host unset, so a
// wallet implementing the Wallet Standard `solana:signIn` feature fills it from the page that
// asked (spec-following wallets bind a page-supplied one to that page too), and the issuer
// refuses any other host for the request's Origin, so a fake page cannot obtain a signature
// that mints for another site. The issuer now
// requires this format on every request that carries an Origin header and checks the signed
// domain against that header; the legacy text stays for the origin-less lane (CLI keypairs,
// the server SDK, React Native, realtime agents).
//
// The layout is the SIWS ABNF exactly as @solana/wallet-standard-util 1.1.3 emits it
// (lib/esm/signIn.js createSignInMessageText, lines 116-175): the domain line, the address,
// a blank line, the statement, a blank line, then the present fields in the fixed order
// URI, Version, Chain ID, Nonce, Issued At, Expiration Time, Not Before, Request ID,
// Resources. A wallet rendering our sign-in request and a guest device key rendering it here
// therefore sign byte-identical text for the same fields, and the issuer parses one grammar.
export const SOLANA_SIGN_IN_STATEMENT = "Sign in to this Bounded app with your Solana wallet.";
export const SOLANA_SIGN_IN_VERSION = "1";
export const SOLANA_SIGN_IN_VALIDITY_MS = 5 * 60 * 1000;
export function createSolanaSignInRequest(appId, nonce, now = Date.now()) {
    return {
        statement: SOLANA_SIGN_IN_STATEMENT,
        version: SOLANA_SIGN_IN_VERSION,
        nonce,
        issuedAt: new Date(now).toISOString(),
        expirationTime: new Date(now + SOLANA_SIGN_IN_VALIDITY_MS).toISOString(),
        requestId: appId,
    };
}
/**
 * The sign-in request for the app the SDK is configured for. Reads the SAME global config
 * appId as genAuthNonce and createSessionWithSignature, so the signed app id equals the app
 * the session is minted for; a snapshotted appIdOverride binds a wallet client created
 * against another app (the same contract genSolanaMessage keeps).
 */
export async function genSolanaSignInRequest(nonce, appIdOverride) {
    const appId = appIdOverride !== null && appIdOverride !== void 0 ? appIdOverride : (await getConfig()).appId;
    return createSolanaSignInRequest(appId, nonce);
}
export function createSolanaSignInMessageText(fields) {
    return `${fields.domain} wants you to sign in with your Solana account:\n${fields.address}\n\n` +
        `${fields.statement}\n\n` +
        `Version: ${fields.version}\n` +
        `Nonce: ${fields.nonce}\n` +
        `Issued At: ${fields.issuedAt}\n` +
        `Expiration Time: ${fields.expirationTime}\n` +
        `Request ID: ${fields.requestId}`;
}
/**
 * The full sign-in text for a signer that is not a wallet (a browser guest device key),
 * which therefore stamps the page host itself.
 */
export async function genSolanaSignInMessage(address, nonce, domain, appIdOverride) {
    return createSolanaSignInMessageText(Object.assign(Object.assign({}, (await genSolanaSignInRequest(nonce, appIdOverride))), { domain, address }));
}
/**
 * The host a browser page signs in as: `location.host`, the hostname plus any non-default
 * port, which is exactly what the issuer compares the Origin header's host against. Null
 * off-browser (Node, React Native), where the caller stays on the origin-less legacy lane.
 */
export function browserSignInDomain() {
    var _a;
    if (typeof window === "undefined")
        return null;
    const host = (_a = window.location) === null || _a === void 0 ? void 0 : _a.host;
    return typeof host === "string" && host ? host : null;
}
